import { allParsedDataTypes, DataTypeSpecifier, ParsedDataTypeSpecifier } from "verdi-common";

import { DATA_TYPE_CATEGORY } from "./categories";

export interface DataType {
    name: string;
    category: string;
    labelTranslationKey?: string;
}

/**
 * Create a mapping for user data types with their labels and categories
 *
 * TODO fix these labels so they're not single long strings, split out the properties for name, unit, manufacturer, etc
 */
export const USER_DATA_TYPES: Partial<Record<DataTypeSpecifier, DataType>> = {
    avgNDVI: { name: "Mean NDVI", category: DATA_TYPE_CATEGORY.NDVI, labelTranslationKey: "ndvi" },
    draginoEC: {
        name: "Electrical conductivity (Dragino) [μS/cm]",
        category: DATA_TYPE_CATEGORY.SOIL_SENSOR,
        labelTranslationKey: "soil-ec",
    },
    soilTemperature: {
        name: "Soil temperature [°C]",
        category: DATA_TYPE_CATEGORY.SOIL_SENSOR,
        labelTranslationKey: "soil-temperature",
    },
    draginoECMoisture: {
        name: "Soil moisture (Dragino) [%]",
        category: DATA_TYPE_CATEGORY.SOIL_MOISTURE,
        labelTranslationKey: "soil-moisture",
    },
    moisture_drillAndDrop: {
        name: "Soil Moisture (multi-depth) [%]",
        category: DATA_TYPE_CATEGORY.SOIL_MOISTURE,
        labelTranslationKey: "soil-moisture",
    },
    salinity_drillAndDrop: {
        name: "Soil Salinity (multi-depth) [%]",
        category: DATA_TYPE_CATEGORY.SOIL_SENSOR,
        labelTranslationKey: "soil-salinity",
    },
    moisture_watermark: {
        name: "Soil moisture (Watermark) [kPa]",
        category: DATA_TYPE_CATEGORY.SOIL_MOISTURE,
        labelTranslationKey: "soil-moisture",
    },
    moisture_irrometer: {
        name: "Soil moisture (Irrometer) [kPa]",
        category: DATA_TYPE_CATEGORY.SOIL_MOISTURE,
        labelTranslationKey: "soil-moisture",
    },
    moisture_vol: {
        name: "Volumetric soil moisture [%]",
        category: DATA_TYPE_CATEGORY.SOIL_MOISTURE,
        labelTranslationKey: "soil-moisture",
    },
    moisture: {
        name: "Soil moisture (Sensoterra) [Sensoterra Index]",
        category: DATA_TYPE_CATEGORY.SOIL_MOISTURE,
        labelTranslationKey: "soil-moisture",
    },
    ndviVariance: { name: "NDVI Variance", category: DATA_TYPE_CATEGORY.NDVI, labelTranslationKey: "ndvi-variance" },
    valveOpenPercent: {
        name: "Valve open percentage [%]",
        category: DATA_TYPE_CATEGORY.IRRIGATION_CONTROL,
        labelTranslationKey: "valve-open-percent",
    },
    valveState: {
        name: "Valve state [binary]",
        category: DATA_TYPE_CATEGORY.IRRIGATION_CONTROL,
        labelTranslationKey: "valve-state",
    },
    v1flowmeter: {
        name: "Flow meter (Verdi) [L/min]",
        category: DATA_TYPE_CATEGORY.WATER_FLOW,
        labelTranslationKey: "flow",
    },
    zoneIrrigationScheduled: {
        name: "Scheduled zone irrigations [binary]",
        category: DATA_TYPE_CATEGORY.IRRIGATION_PLANNING,
        labelTranslationKey: "scheduled-zone-irrigations",
    },
    temperature: {
        name: "Ambient temperature [°C]",
        category: DATA_TYPE_CATEGORY.ENVIRONMENTAL,
        labelTranslationKey: "ambient-temperature",
    },
    v1transducer: {
        name: "Pressure transducer (Verdi) [kPa]",
        category: DATA_TYPE_CATEGORY.PRESSURE_SENSOR,
        labelTranslationKey: "pressure",
    },
    waterTankLevel: {
        name: "Water tank level [m]",
        category: DATA_TYPE_CATEGORY.IRRIGATION_PLANNING,
        labelTranslationKey: "water-tank-level",
    },
    stemWaterPotential: {
        name: "Stem water potential [kPa]",
        category: DATA_TYPE_CATEGORY.WATER_FLOW,
        labelTranslationKey: "stem-water-potential",
    },
    voltage: { name: "Voltage [V]", category: DATA_TYPE_CATEGORY.ENVIRONMENTAL, labelTranslationKey: "voltage" },
} as const;

// Create a mapping for all data types with those not in USER_DATA_TYPES marked as admin-only
export const ALL_DATA_TYPES: Partial<Record<ParsedDataTypeSpecifier, DataType>> = Object.fromEntries(
    allParsedDataTypes.map((dataType) => {
        if (USER_DATA_TYPES[dataType as keyof typeof USER_DATA_TYPES]) {
            return [dataType, USER_DATA_TYPES[dataType as keyof typeof USER_DATA_TYPES]];
        }
        return [dataType, { name: dataType, category: DATA_TYPE_CATEGORY.ADMIN_ONLY }];
    }),
);
